import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Card,
  CardContent,
  Chip,
  Dialog,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  FormControlLabel,
  Paper,
  Switch,
  Tab,
  Tabs,
  Typography,
  makeStyles
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";
import SwapVertIcon from "@material-ui/icons/SwapVert";
import { formatWhatsappTime } from "../../helpers/formatWhatsappTime";
import Ticket from "../../components/Ticket";
import { useHistory } from "react-router-dom";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import TicketsKanban from "../../components/TicketsKanban";
import TicketsQueueSelect from "../../components/TicketsQueueSelect";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";

const hexToRgba = (hex, alpha) => {
  const normalized = String(hex || "#1976d2").replace("#", "");
  const safeHex = normalized.length === 3
    ? normalized.split("").map(char => char + char).join("")
    : normalized.padEnd(6, "0").slice(0, 6);

  const red = parseInt(safeHex.slice(0, 2), 16);
  const green = parseInt(safeHex.slice(2, 4), 16);
  const blue = parseInt(safeHex.slice(4, 6), 16);

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

const useStyles = makeStyles(theme => ({
  page: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    height: "100%"
  },
  intro: {
    color: theme.palette.text.secondary
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    borderRadius: 14,
    flexWrap: "wrap"
  },
  toolbarLeft: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    flexWrap: "wrap"
  },
  toolbarRight: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap"
  },
  searchField: {
    minWidth: 240,
    background: "#fff"
  },
  sortSelect: {
    minWidth: 160,
    background: "#fff"
  },
  tabsShell: {
    padding: theme.spacing(1),
    borderRadius: 16,
    border: `1px solid ${hexToRgba("#1d4ed8", 0.12)}`,
    background: `linear-gradient(135deg, ${hexToRgba("#ffffff", 0.96)} 0%, ${hexToRgba("#eff6ff", 0.94)} 100%)`,
    boxShadow: `0 18px 40px ${hexToRgba("#0f172a", 0.08)}`
  },
  boardShell: {
    flex: 1,
    minHeight: 0,
    padding: theme.spacing(1),
    borderRadius: 18,
    overflow: "hidden"
  },
  pipelineBoard: {
    background: `linear-gradient(180deg, ${hexToRgba("#dbeafe", 0.45)} 0%, ${theme.palette.background.default} 42%)`,
    borderRadius: 16,
    padding: theme.spacing(2),
    minHeight: "100%"
  },
  boardScroller: {
    overflowX: "auto",
    paddingBottom: theme.spacing(1)
  },
  boardRow: {
    display: "grid",
    gridAutoFlow: "column",
    gridAutoColumns: "minmax(340px, 1fr)",
    gap: theme.spacing(2.5),
    alignItems: "start"
  },
  column: {
    position: "relative",
    overflow: "hidden",
    background: `linear-gradient(180deg, ${hexToRgba("#ffffff", 0.98)} 0%, ${hexToRgba("#f8fafc", 0.98)} 100%)`,
    borderRadius: 18,
    padding: theme.spacing(2.5),
    minHeight: "calc(100vh - 300px)",
    display: "flex",
    flexDirection: "column",
    border: `1px solid ${hexToRgba("#0f172a", 0.08)}`,
    boxShadow: `0 20px 50px ${hexToRgba("#0f172a", 0.10)}`
  },
  columnAccent: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 6
  },
  columnHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5)
  },
  columnHeading: {
    minWidth: 0
  },
  columnTitle: {
    fontWeight: 700
  },
  columnSubtitle: {
    color: theme.palette.text.secondary
  },
  columnBody: {
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.25),
    paddingTop: theme.spacing(1),
    maxHeight: "calc(100vh - 380px)",
    minHeight: 0,
    ...theme.scrollbarStyles
  },
  ticketCard: {
    cursor: "grab",
    borderRadius: 14,
    border: `1px solid ${hexToRgba("#0f172a", 0.06)}`,
    borderLeft: "4px solid transparent",
    boxShadow: `0 10px 24px ${hexToRgba("#0f172a", 0.08)}`,
    flex: "0 0 auto",
    minHeight: 112
  },
  ticketContent: {
    paddingBottom: `${theme.spacing(2)}px !important`,
    minWidth: 0
  },
  ticketTopBar: {
    height: 6,
    borderRadius: 999,
    marginBottom: theme.spacing(1.5)
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1)
  },
  cardAvatar: {
    width: 34,
    height: 34,
    flexShrink: 0,
    fontSize: 15
  },
  cardHeaderText: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column"
  },
  cardNumber: {
    color: theme.palette.text.secondary,
    lineHeight: 1.2
  },
  cardTime: {
    flexShrink: 0,
    color: theme.palette.text.secondary,
    fontSize: 11
  },
  ticketTitle: {
    fontWeight: 600,
    wordBreak: "break-word"
  },
  lastMessage: {
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    color: theme.palette.text.secondary,
    wordBreak: "break-word",
    marginTop: theme.spacing(0.5)
  },
  ticketMeta: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginTop: theme.spacing(1)
  },
  emptyState: {
    color: theme.palette.text.secondary,
    textAlign: "center",
    padding: theme.spacing(4, 2),
    border: `1px dashed ${hexToRgba("#94a3b8", 0.45)}`,
    borderRadius: 14,
    background: hexToRgba("#ffffff", 0.8)
  },
  ticketDialogPaper: {
    height: "85vh",
    maxHeight: "85vh",
    overflow: "hidden",
    position: "relative"
  },
  ticketDialogClose: {
    position: "absolute",
    top: 6,
    right: 6,
    zIndex: 10,
    backgroundColor: "rgba(255, 255, 255, 0.85)"
  }
}));

const isPrincipalPipeline = pipeline =>
  String(pipeline?.name || "").trim().toLowerCase() === "pipeline principal";

const Kanban = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const userQueueIds = user.queues.map(queue => queue.id);
  const [selectedQueueIds, setSelectedQueueIds] = useState(userQueueIds || []);
  const [showAllTickets, setShowAllTickets] = useState(false);
  const [pipelines, setPipelines] = useState([]);
  const [selectedPipelineId, setSelectedPipelineId] = useState("");
  const [tickets, setTickets] = useState([]);
  const [contactMemberships, setContactMemberships] = useState([]);
  const [modalTicketId, setModalTicketId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("lastMessage");
  const [sortDir, setSortDir] = useState("desc");

  // Filtro geral (nome/telefone/indicacao/ultima mensagem) + ordenacao
  // aplicados aos cards de cada etapa do funil.
  const applySearchSort = list => {
    const term = String(searchTerm || "").trim().toLowerCase();
    let out = list;
    if (term) {
      out = out.filter(item => {
        const hay = [
          item.contact?.name,
          item.contact?.number,
          item.contact?.partner,
          item.lastMessage
        ]
          .map(v => String(v || "").toLowerCase())
          .join(" ");
        return hay.includes(term);
      });
    }
    const dir = sortDir === "asc" ? 1 : -1;
    const dateOf = it =>
      new Date(it.lastMessageAt || it.updatedAt || 0).getTime();
    const stageTimeOf = it =>
      new Date(it.updatedAt || it.createdAt || 0).getTime();
    const strOf = (it, pick) => String(pick(it) || "").toLowerCase();
    return [...out].sort((a, b) => {
      if (sortBy === "name") {
        return (
          dir *
          strOf(a, it => it.contact?.name || it.contact?.number).localeCompare(
            strOf(b, it => it.contact?.name || it.contact?.number)
          )
        );
      }
      if (sortBy === "number") {
        return (
          dir *
          strOf(a, it => it.contact?.number).localeCompare(
            strOf(b, it => it.contact?.number)
          )
        );
      }
      if (sortBy === "stageTime") {
        return dir * (stageTimeOf(a) - stageTimeOf(b));
      }
      return dir * (dateOf(a) - dateOf(b));
    });
  };

  useEffect(() => {
    if (user.profile?.toUpperCase() === "ADMIN") {
      setShowAllTickets(true);
    }
  }, [user.profile]);

  const selectedPipeline = useMemo(
    () => pipelines.find(item => String(item.id) === String(selectedPipelineId)),
    [pipelines, selectedPipelineId]
  );

  const loadBoard = async pipelineId => {
    try {
      const { data: pipelinesData } = await api.get("/kanban-pipelines");

      setPipelines(pipelinesData);

      const effectivePipelineId =
        pipelineId || selectedPipelineId || (pipelinesData[0] ? String(pipelinesData[0].id) : "");

      if (!selectedPipelineId && pipelinesData.length) {
        setSelectedPipelineId(String(pipelinesData[0].id));
      }

      const effectivePipeline = pipelinesData.find(
        item => String(item.id) === String(effectivePipelineId)
      );

      if (isPrincipalPipeline(effectivePipeline)) {
        const { data: ticketsData } = await api.get("/tickets", {
          params: {
            showAll: showAllTickets,
            queueIds: JSON.stringify(selectedQueueIds || []),
            ...(effectivePipelineId ? { pipelineId: effectivePipelineId } : {})
          }
        });
        setTickets(ticketsData.tickets || []);
        setContactMemberships([]);
        return;
      }

      const { data: membershipsData } = await api.get("/contact-pipeline-memberships", {
        params: {
          pipelineId: effectivePipelineId
        }
      });
      setContactMemberships(membershipsData.memberships || []);
      setTickets([]);
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    loadBoard(selectedPipelineId);
  }, [selectedPipelineId, selectedQueueIds, showAllTickets]);

  useEffect(() => {
    const socket = openSocket();
    const reload = () => loadBoard(selectedPipelineId);

    socket.on("ticket", reload);
    socket.on("appMessage", reload);
    socket.on("contact", reload);

    return () => {
      socket.disconnect();
    };
  }, [selectedPipelineId, selectedQueueIds, showAllTickets]);

  const handleDrop = async (event, stage) => {
    event.preventDefault();
    const ticketId = event.dataTransfer.getData("ticketId");
    const ticketUserId = event.dataTransfer.getData("ticketUserId");
    const contactId = event.dataTransfer.getData("contactId");

    if (!contactId && !ticketId) {
      return;
    }

    // move OTIMISTA: o card troca de coluna na hora, sem recarregar o board.
    const prevMemberships = contactMemberships;
    const prevTickets = tickets;
    if (contactId) {
      setContactMemberships(prev =>
        prev.map(m =>
          String(m.contactId || m.contact?.id) === String(contactId)
            ? { ...m, kanbanStageId: stage.id }
            : m
        )
      );
    } else if (ticketId) {
      setTickets(prev =>
        prev.map(t =>
          String(t.id) === String(ticketId)
            ? { ...t, kanbanStageId: stage.id }
            : t
        )
      );
    }

    try {
      if (contactId) {
        await api.put(`/contacts/${contactId}/pipelines/${stage.pipelineId}`, {
          pipelineId: stage.pipelineId,
          kanbanStageId: stage.id
        });
      } else if (ticketId) {
        await api.put(`/tickets/${ticketId}`, {
          userId: ticketUserId ? Number(ticketUserId) : null,
          pipelineId: stage.pipelineId,
          kanbanStageId: stage.id
        });
      }
    } catch (err) {
      setContactMemberships(prevMemberships);
      setTickets(prevTickets);
      toastError(err);
    }
  };

  const openContactTicket = async membership => {
    const contactId = membership?.contactId || membership?.contact?.id;

    if (!contactId) {
      return;
    }

    try {
      const { data } = await api.get("/tickets", {
        params: {
          showAll: true,
          contactId
        }
      });
      const tickets = data.tickets || [];
      const ticket =
        tickets.find(item => ["open", "pending"].includes(item.status)) ||
        tickets[0];

      if (ticket?.id) {
        setModalTicketId(ticket.id);
      }
    } catch (err) {
      toastError(err);
    }
  };

  const renderPipelineBoard = () => {
    if (!selectedPipeline) {
      return (
        <div className={classes.emptyState}>
          Nenhum pipeline encontrado. Cadastre um pipeline e as etapas em "Pipelines".
        </div>
      );
    }

    if (isPrincipalPipeline(selectedPipeline)) {
      return (
        <TicketsKanban
          showAll={showAllTickets}
          selectedQueueIds={selectedQueueIds}
        />
      );
    }

    return (
      <div className={classes.pipelineBoard}>
        <div className={classes.boardScroller}>
          <div className={classes.boardRow}>
            {(selectedPipeline.stages || []).map(stage => {
              const stageTickets = applySearchSort(
                isPrincipalPipeline(selectedPipeline)
                  ? tickets.filter(ticket => ticket.kanbanStageId === stage.id)
                  : contactMemberships.filter(
                      membership => membership.kanbanStageId === stage.id
                    )
              );
              const accentColor = stage.color || "#1976d2";

              return (
                <Paper
                  key={stage.id}
                  className={classes.column}
                  onDragOver={event => event.preventDefault()}
                  onDrop={event => handleDrop(event, stage)}
                >
                  <div
                    className={classes.columnAccent}
                    style={{
                      background: `linear-gradient(90deg, ${accentColor} 0%, ${hexToRgba(accentColor, 0.55)} 100%)`
                    }}
                  />
                  <div className={classes.columnHeader}>
                    <div className={classes.columnHeading}>
                      <Typography variant="h6" className={classes.columnTitle}>
                        {stage.name}
                      </Typography>
                      <Typography variant="body2" className={classes.columnSubtitle}>
                        Pipeline: {selectedPipeline.name}
                      </Typography>
                    </div>
                    <Chip
                      size="small"
                      label={stageTickets.length}
                      style={{
                        backgroundColor: accentColor,
                        color: "#fff",
                        fontWeight: 700
                      }}
                    />
                  </div>
                  <div className={classes.columnBody}>
                    {stageTickets.length === 0 ? (
                      <div className={classes.emptyState}>
                        {isPrincipalPipeline(selectedPipeline)
                          ? "Nenhuma conversa nesta etapa."
                          : "Nenhum contato nesta etapa."}
                      </div>
                    ) : (
                      stageTickets.map(item => (
                        <Card
                          key={isPrincipalPipeline(selectedPipeline) ? item.id : `${item.contactId}-${item.pipelineId}`}
                          className={classes.ticketCard}
                          style={{ borderLeftColor: accentColor }}
                          draggable
                          onDragStart={event => {
                            if (isPrincipalPipeline(selectedPipeline)) {
                              event.dataTransfer.setData("ticketId", String(item.id));
                              event.dataTransfer.setData(
                                "ticketUserId",
                                item.userId ? String(item.userId) : ""
                              );
                            } else {
                              event.dataTransfer.setData("contactId", String(item.contactId));
                            }
                          }}
                          onClick={() => {
                            if (isPrincipalPipeline(selectedPipeline)) {
                              setModalTicketId(item.id);
                              return;
                            }
                            openContactTicket(item);
                          }}
                        >
                          <CardContent className={classes.ticketContent}>
                            <div className={classes.cardHeader}>
                              <Avatar
                                src={item.contact?.profilePicUrl}
                                className={classes.cardAvatar}
                              >
                                {(item.contact?.name || item.contact?.number || "?")
                                  .charAt(0)
                                  .toUpperCase()}
                              </Avatar>
                              <div className={classes.cardHeaderText}>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "baseline",
                                    justifyContent: "space-between",
                                    gap: 8
                                  }}
                                >
                                  <Typography
                                    variant="subtitle2"
                                    className={classes.ticketTitle}
                                    noWrap
                                  >
                                    {item.contact?.name ||
                                      item.contact?.number ||
                                      "Contato sem nome"}
                                  </Typography>
                                  {(item.lastMessageAt || item.updatedAt) && (
                                    <span className={classes.cardTime}>
                                      {formatWhatsappTime(
                                        item.lastMessageAt || item.updatedAt
                                      )}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            {(item.ticketStatus || item.status) && (
                              <span
                                style={{
                                  display: "inline-block",
                                  fontSize: "0.66rem",
                                  fontWeight: 700,
                                  padding: "1px 8px",
                                  borderRadius: 10,
                                  marginTop: 4,
                                  marginRight: 6,
                                  letterSpacing: 0.3,
                                  background:
                                    (item.ticketStatus || item.status) ===
                                    "closed"
                                      ? "#eceff1"
                                      : "#e8f5e9",
                                  color:
                                    (item.ticketStatus || item.status) ===
                                    "closed"
                                      ? "#546e7a"
                                      : "#2e7d32"
                                }}
                              >
                                {(item.ticketStatus || item.status) ===
                                "closed"
                                  ? "Fechado"
                                  : "Aberto"}
                              </span>
                            )}
                            {item.contact?.partner ? (
                              <span
                                style={{
                                  display: "inline-block",
                                  maxWidth: "100%",
                                  background: "#e0f2f1",
                                  color: "#00695c",
                                  fontSize: "0.72rem",
                                  fontWeight: 600,
                                  padding: "1px 8px",
                                  borderRadius: 10,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  marginTop: 4
                                }}
                              >
                                🤝 {item.contact.partner}
                              </span>
                            ) : null}
                            <Typography
                              variant="body2"
                              className={classes.lastMessage}
                            >
                              {item.lastMessage || "Sem última mensagem"}
                            </Typography>
                            {item.note && (
                              <Typography
                                variant="caption"
                                style={{
                                  display: "block",
                                  marginTop: 4,
                                  color: "#c62828",
                                  whiteSpace: "pre-wrap",
                                  lineHeight: 1.3
                                }}
                              >
                                {item.note}
                              </Typography>
                            )}
                            <div className={classes.ticketMeta}>
                              {(item.contact?.tags || []).map(tag => (
                                <Chip
                                  key={tag.id}
                                  size="small"
                                  label={tag.name}
                                  style={{ backgroundColor: tag.color, color: "#fff" }}
                                />
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </div>
                </Paper>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Kanban</Title>
      </MainHeader>

      <div className={classes.page}>
        <Typography variant="body2" className={classes.intro}>
          O Pipeline Principal espelha o fluxo operacional dos tickets. Os outros pipelines continuam com estágios próprios.
        </Typography>

        <Paper elevation={0} variant="outlined" className={classes.toolbar}>
          <div className={classes.toolbarLeft}>
            <Can
              role={user.profile}
              perform="tickets-manager:showall"
              yes={() => (
                <FormControlLabel
                  label={i18n.t("tickets.buttons.showAll")}
                  labelPlacement="start"
                  control={
                    <Switch
                      size="small"
                      checked={showAllTickets}
                      onChange={() => setShowAllTickets(prevState => !prevState)}
                      name="showAllTickets"
                      color="primary"
                    />
                  }
                />
              )}
            />
            <TicketsQueueSelect
              selectedQueueIds={selectedQueueIds}
              userQueues={user?.queues}
              onChange={values => setSelectedQueueIds(values)}
            />
          </div>
          <div className={classes.toolbarRight}>
            <TextField
              size="small"
              variant="outlined"
              placeholder="Buscar nome, telefone, indicacao, mensagem..."
              value={searchTerm}
              onChange={event => setSearchTerm(event.target.value)}
              className={classes.searchField}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
            />
            <Select
              size="small"
              variant="outlined"
              value={sortBy}
              onChange={event => setSortBy(event.target.value)}
              className={classes.sortSelect}
            >
              <MenuItem value="lastMessage">Ultima mensagem</MenuItem>
              <MenuItem value="stageTime">Tempo na etapa</MenuItem>
              <MenuItem value="name">Nome</MenuItem>
              <MenuItem value="number">Numero</MenuItem>
            </Select>
            <IconButton
              size="small"
              onClick={() =>
                setSortDir(prev => (prev === "asc" ? "desc" : "asc"))
              }
              title={
                sortDir === "asc"
                  ? "Crescente / mais antigo primeiro (clique p/ inverter)"
                  : "Decrescente / mais recente primeiro (clique p/ inverter)"
              }
            >
              <SwapVertIcon fontSize="small" />
            </IconButton>
          </div>
        </Paper>

        <Paper className={classes.tabsShell}>
          <Tabs
            value={selectedPipelineId}
            onChange={(_, value) => setSelectedPipelineId(String(value))}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
          >
            {pipelines.map(pipeline => (
              <Tab
                key={pipeline.id}
                value={String(pipeline.id)}
                label={pipeline.name}
              />
            ))}
          </Tabs>
        </Paper>

        <Paper elevation={0} variant="outlined" className={classes.boardShell}>
          {renderPipelineBoard()}
        </Paper>
      </div>

      <Dialog
        open={Boolean(modalTicketId)}
        onClose={() => setModalTicketId(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ className: classes.ticketDialogPaper }}
      >
        <IconButton
          size="small"
          className={classes.ticketDialogClose}
          onClick={() => setModalTicketId(null)}
        >
          <CloseIcon />
        </IconButton>
        {modalTicketId && (
          <Ticket key={modalTicketId} ticketId={String(modalTicketId)} onBack={() => setModalTicketId(null)} />
        )}
      </Dialog>
    </MainContainer>
  );
};

export default Kanban;
